import React, { useState, useEffect } from "react"
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Collapse,
  Badge,
} from "reactstrap"
import moment from "moment"
import { UpdateCPSRequest } from "helpers/backend_helper"
import { useLoading } from "helpers/custom_hooks"

const CPSSampleApproval = ({ entry, myUser, statusIndex, reload }) => {
  const setLoading = useLoading()
  const [isApproveOpen, setIsApproveOpen] = useState(false)
  const [isDeclineOpen, setIsDeclineOpen] = useState(false)
  const [isReleasing, setIsReleasing] = useState(false)
  const [isDeclining, setIsDeclining] = useState(false)
  const [declineReason, setDeclineReason] = useState("")

  // Determine if this step is active based on entry statusIndex
  const isActive =
    entry?.statusIndex === statusIndex ||
    (entry?.sampleDeclined === 1 && entry?.statusIndex < statusIndex)
  const isCompleted =
    entry?.statusIndex > statusIndex && entry?.sampleDeclined !== 1
  const isCustomer = myUser?.company?.id === entry?.requestorCompanyID
  const isCPSLab = myUser?.company?.id === entry?.cpsLabID

  // Customer can approve samples, but not if they are also the CPS Lab
  // (in that case, they should use the CPS Lab workflow)
  const canApprove = isActive && !isCompleted && isCustomer && !isCPSLab

  // If user is both CPS Lab and Customer, auto-approve samples
  const shouldAutoApprove = isActive && !isCompleted && isCustomer && isCPSLab

  // Manual approve for users who are both CPS Lab and Customer
  const canManualApprove = isActive && !isCompleted && isCustomer && isCPSLab

  const handleReleaseSamples = async e => {
    e.preventDefault()
    setIsReleasing(true)
    setLoading(true)

    try {
      // Update the request to move to next status
      const updatedEntry = {
        ...entry,
        sampleApprovedDate: new Date().toISOString(),
        sampleApprovedByUserID: myUser?.id,
        statusIndex: statusIndex + 1, // Move to next step
      }
      await UpdateCPSRequest(updatedEntry)

      setIsReleasing(false)
      setLoading(false)
      setIsApproveOpen(false)

      // Small delay to ensure backend has processed the update
      setTimeout(() => {
        reload()
      }, 500)
    } catch (error) {
      console.error("Error releasing samples:", error)
      setIsReleasing(false)
      setLoading(false)
      alert("Error releasing samples. Please try again.")
    }
  }

  const handleDeclineSamples = async e => {
    e.preventDefault()
    if (!declineReason.trim()) {
      alert("Please provide a reason for declining the samples.")
      return
    }

    setIsDeclining(true)
    setLoading(true)

    try {
      // Update the request to go back to Sample Production
      const updatedEntry = {
        ...entry,
        sampleDeclined: true,
        sampleDeclinedDate: new Date().toISOString(),
        sampleDeclineReason: declineReason.trim(),
        statusIndex: 6, // Go back to Sample Production
      }
      await UpdateCPSRequest(updatedEntry)

      setIsDeclining(false)
      setLoading(false)
      setIsDeclineOpen(false)
      setDeclineReason("")

      // Small delay to ensure backend has processed the update
      setTimeout(() => {
        reload()
      }, 500)
    } catch (error) {
      console.error("Error declining samples:", error)
      setIsDeclining(false)
      setLoading(false)
      alert("Error declining samples. Please try again.")
    }
  }

  return (
    <li className={"event-list " + (isActive ? "active" : "")}>
      <div
        className={"event-timeline-dot " + (isActive ? "bx-fade-right" : "")}
      >
        <i className="bx bx-right-arrow-circle font-size-18"></i>
      </div>
      <div className="d-flex">
        <div className="flex-shrink-0 me-3">
          <h5 className="font-size-14">
            {isCompleted
              ? moment(entry?.sampleApprovedDate).format("DD.MM.YYYY")
              : ""}{" "}
            <i className="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
          </h5>
        </div>
        <div className="flex-grow-1">
          <div>
            Sample Approval{" "}
            {isActive
              ? ` - ready to approve ${
                  entry?.sampleQuantity || 25
                } sample pieces`
              : isCompleted
              ? ` - ${entry?.sampleQuantity || 25} sample pieces approved`
              : ""}
          </div>
        </div>
      </div>

      {isActive && (canApprove || canManualApprove) && (
        <div className="mt-3">
          {canManualApprove && (
            <div className="alert alert-info mb-3">
              <i className="bx bx-info-circle me-2"></i>
              You are both the CPS Lab and Customer for this request. You can
              approve the samples to continue the workflow.
            </div>
          )}
          <div className="d-flex gap-2 mb-2">
            <Button
              color="success"
              size="sm"
              onClick={() => {
                setIsApproveOpen(!isApproveOpen)
                setIsDeclineOpen(false)
              }}
            >
              <i className="bx bx-check font-size-12 me-1"></i>
              Approve Samples
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => {
                setIsDeclineOpen(!isDeclineOpen)
                setIsApproveOpen(false)
              }}
            >
              <i className="bx bx-x font-size-12 me-1"></i>
              Decline Samples
            </Button>
          </div>

          <Collapse isOpen={isApproveOpen}>
            <Form onSubmit={handleReleaseSamples}>
              <div className="alert alert-info">
                <i className="bx bx-info-circle me-2"></i>
                Please review the sample report before approving. Once approved,
                production will begin.
              </div>

              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  color="success"
                  size="sm"
                  disabled={isReleasing}
                >
                  {isReleasing ? "Approving..." : "Approve Samples"}
                </Button>
                <Button
                  type="button"
                  color="secondary"
                  size="sm"
                  onClick={() => setIsApproveOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Collapse>

          <Collapse isOpen={isDeclineOpen}>
            <Form onSubmit={handleDeclineSamples}>
              <div className="alert alert-warning">
                <i className="bx bx-info-circle me-2"></i>
                If you decline the samples, the request will go back to sample
                production. Please provide a reason for declining.
              </div>

              <FormGroup>
                <Label for="declineReason">Reason for declining samples:</Label>
                <Input
                  type="textarea"
                  id="declineReason"
                  value={declineReason}
                  onChange={e => setDeclineReason(e.target.value)}
                  placeholder="Please explain why the samples are not acceptable..."
                  rows={3}
                  required
                />
              </FormGroup>

              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  color="danger"
                  size="sm"
                  disabled={isDeclining || !declineReason.trim()}
                >
                  {isDeclining ? "Declining..." : "Decline Samples"}
                </Button>
                <Button
                  type="button"
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    setIsDeclineOpen(false)
                    setDeclineReason("")
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Collapse>
        </div>
      )}

      {isCompleted && (
        <div className="mt-3">
          <div className="d-flex align-items-center">
            <div className="me-2">
              <Badge color="success">Approved</Badge>
            </div>
            <div>
              <small className="text-muted">
                Samples approved on{" "}
                {moment(entry?.sampleApprovedDate).format("DD.MM.YYYY")}
                {entry?.sampleApprovedBy && (
                  <>
                    {" "}
                    by <strong>{entry.sampleApprovedBy}</strong>
                  </>
                )}
              </small>
            </div>
          </div>
          {entry?.sampleReportURL && (
            <div className="mt-2">
              <a
                href={entry.sampleReportURL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary"
              >
                <i className="bx bx-download me-1"></i>
                Download Sample Report
              </a>
            </div>
          )}
        </div>
      )}

      {entry?.sampleDeclined === 1 && entry?.statusIndex <= statusIndex && (
        <div className="mt-3">
          <div className="d-flex align-items-center">
            <div className="me-2">
              <Badge color="danger">Declined</Badge>
            </div>
            <div>
              <small className="text-muted">
                Samples declined on{" "}
                {moment(entry?.sampleDeclinedDate).format("DD.MM.YYYY")}
              </small>
            </div>
          </div>
          {entry?.sampleDeclineReason && (
            <div className="mt-2">
              <div className="alert alert-danger">
                <strong>Reason:</strong> {entry.sampleDeclineReason}
              </div>
            </div>
          )}
        </div>
      )}

      {isActive && !canApprove && (
        <div className="mt-3">
          <div className="alert alert-warning">
            <i className="bx bx-time-five me-2"></i>
            Waiting for customer approval of samples.
          </div>
          {entry?.sampleReportURL && (
            <div className="mt-2">
              <a
                href={entry.sampleReportURL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary"
              >
                <i className="bx bx-download me-1"></i>
                Download Sample Report
              </a>
            </div>
          )}
        </div>
      )}

      <p className="mb-0 mt-2">
        <i className="bx bx-time-five font-size-13 align-middle me-1"></i>
        <span className="font-size-13">
          {isCompleted
            ? "Samples approved"
            : isActive
            ? "Ready for approval"
            : "Not started"}
        </span>
      </p>
    </li>
  )
}

export default CPSSampleApproval
